import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import {
    Layer,
    LayerAttribute,
    LayerControllerGet200Response,
    LayerControllerGetRequestParams,
    LayersService,
    LayerWithAttributes,
} from '@connect-services/client/sdk/api';
import { defaultPaginationParameters, paginationMaxTake } from '@shared/client/models/pagination.model';

export const excludedAttributes: string[] = ['businessIdentifier', 'layerId', 'technicalIdentifier', 'uuid'];

export const readOnlyAttributes: string[] = ['zn_uuid', 'ogc_fid', 'deposit_id', 'geom'];

export type AllLayersFilter = {
    depositFolder?: string;
    geometryType?: Layer.GeometryTypeEnum;
    hasGeom?: boolean;
    layers?: LayerControllerGet200Response;
    order?: 'ASC' | 'DESC';
    orderBy?: string;
    searchValue?: string;
    types?: Layer.TypeEnum[];
};

export const labelByLayerType: Record<string, string> = {
    [LayerWithAttributes.TypeEnum.GEOM]: 'geom',
    [LayerWithAttributes.TypeEnum.INTERSTANDARD]: 'interstandard',
    [LayerWithAttributes.TypeEnum.TABLE]: 'deposit',
    [LayerWithAttributes.TypeEnum.WFS]: 'Web Feature Service',
    [LayerWithAttributes.TypeEnum.WMS]: 'Web Map Service',
    [LayerWithAttributes.TypeEnum.WMTS]: 'Web Map Tile Service',
};

export enum GeomFilter {
    ONLY_GEOM = 'ONLY_GEOM',
    ONLY_NOGEOM = 'ONLY_NOGEOM',
    ALL = 'ALL',
}

type GeographicFilterLayerModalList = {
    layerIdsByName: Record<string, string[]>;
    lineStringLayerIdsByName: Record<string, string[]>;
    polygonLayerIdsByName: Record<string, string[]>;
};

@Injectable({
    providedIn: 'root',
})
export class LayerService {
    public readonly forceLayerListRefresh = signal(0);
    public readonly layersGrouped = signal<Record<string, string[]>>({});
    public readonly lineStringLayersGrouped = signal<Record<string, string[]>>({});
    public readonly polygonLayersGrouped = signal<Record<string, string[]>>({});

    protected readonly translocoService = inject(TranslocoService);

    private readonly layerListBloc = inject(LayerListBloc);
    private readonly layersService = inject(LayersService);
    private readonly projectBloc = inject(ProjectBloc);

    public async getAllLayers(allLayersFilter?: AllLayersFilter): Promise<LayerControllerGet200Response> {
        if (allLayersFilter?.layers && !allLayersFilter.layers.hasNextPage) {
            return allLayersFilter.layers;
        }

        const page: number = allLayersFilter?.layers ? allLayersFilter.layers.page + 1 : 0;
        const loadedLayers = await firstValueFrom(
            this.layersService.layerControllerGet({
                depositFolder: allLayersFilter?.depositFolder,
                searchValue: allLayersFilter?.searchValue,
                ...defaultPaginationParameters,
                geomFilter: this.mapHasGeomToGeomFilter(allLayersFilter?.hasGeom),
                geometryType: allLayersFilter?.geometryType,
                isCaseSensitive: true,
                order: allLayersFilter?.order,
                orderBy: allLayersFilter?.orderBy,
                page,
                project: this.projectBloc.value()?.id,
                take: paginationMaxTake,
                types: allLayersFilter?.types,
            }),
        );

        loadedLayers.items.unshift(...(allLayersFilter?.layers?.items ?? []));

        return this.getAllLayers({ ...allLayersFilter, layers: loadedLayers });
    }

    public async getAttributes(layer: Layer): Promise<LayerAttribute[]> {
        if (![Layer.TypeEnum.TABLE, Layer.TypeEnum.INTERSTANDARD, Layer.TypeEnum.REFERENCE_DATA].includes(layer.type)) {
            return [];
        }

        let attributes = await firstValueFrom(this.layersService.layerControllerGetAttributes({ id: layer.id }));

        attributes = attributes.filter((attribute: LayerAttribute) => !excludedAttributes.includes(attribute.column));

        return this.sortAttributes(attributes);
    }

    public getGroupedLayers(layers: Layer[], withTabularLayers: boolean = true): Record<string, string[]> {
        if (!this.layerListBloc.value()) {
            throw new Error('No layers found');
        }

        return layers.reduce((acc: Record<string, string[]>, layer: Layer) => {
            if (!layer.hasGeom && !withTabularLayers) {
                return acc;
            }

            if (!acc[layer.name]) {
                acc[layer.name] = [];
            }

            acc[layer.name].push(layer.id);

            return acc;
        }, {});
    }

    public setLayers(layerSources: Layer[]): void {
        this.layersGrouped.set({});

        const { layerIdsByName, lineStringLayerIdsByName, polygonLayerIdsByName }: GeographicFilterLayerModalList =
            layerSources.reduce(
                (acc: GeographicFilterLayerModalList, layer: Layer) => {
                    if (!acc.layerIdsByName[layer.name]) {
                        acc.layerIdsByName[layer.name] = [];
                    }

                    acc.layerIdsByName[layer.name].push(layer.id);

                    if (layer.geometryType === Layer.GeometryTypeEnum.LINE_STRING) {
                        if (!acc.lineStringLayerIdsByName[layer.name]) {
                            acc.lineStringLayerIdsByName[layer.name] = [];
                        }

                        acc.lineStringLayerIdsByName[layer.name].push(layer.id);

                        return acc;
                    }

                    if (layer.geometryType !== Layer.GeometryTypeEnum.POLYGON) {
                        return acc;
                    }

                    if (!acc.polygonLayerIdsByName[layer.name]) {
                        acc.polygonLayerIdsByName[layer.name] = [];
                    }

                    acc.polygonLayerIdsByName[layer.name].push(layer.id);

                    return acc;
                },
                { layerIdsByName: {}, lineStringLayerIdsByName: {}, polygonLayerIdsByName: {} },
            );

        this.layersGrouped.set(layerIdsByName);
        this.polygonLayersGrouped.set(polygonLayerIdsByName);
        this.lineStringLayersGrouped.set(lineStringLayerIdsByName);
    }

    private mapHasGeomToGeomFilter(hasGeom?: boolean): LayerControllerGetRequestParams['geomFilter'] {
        if (hasGeom === undefined) {
            return GeomFilter.ALL;
        }

        return hasGeom ? GeomFilter.ONLY_GEOM : GeomFilter.ONLY_NOGEOM;
    }

    private sortAttributes(layersAttributes: LayerAttribute[]): LayerAttribute[] {
        return layersAttributes.toSorted((attribute1: LayerAttribute, attribute2: LayerAttribute) => {
            if (attribute1.order && attribute2.order) {
                return attribute1.order - attribute2.order;
            }

            if (attribute1.order) {
                return -1;
            }

            if (attribute2.order) {
                return 1;
            }

            return 0;
        });
    }
}
